import { Component, OnDestroy, OnInit, computed, effect, inject, input, output, signal } from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { Location, Room } from '@era/locations-sdk';
import { CategoryStoreSelectors, SelectedLocation } from '@era/stores';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { LocationSelectContainerComponent } from '../../select-location/location-select/location-select.container';
import { RoomSelectContainerComponent } from '../../select-location/room-select/room-select.container';

@Component({
  selector: 'era-device-link-selector',
  templateUrl: './device-link-selector.component.html',
  imports: [LocationSelectContainerComponent, ReactiveFormsModule, RoomSelectContainerComponent],
})
export class DeviceLinkSelectorComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);

  public readonly selection = input.required<SelectedLocation>();
  public readonly isSpare = input<boolean | undefined>(undefined);
  public readonly isHousekeeping = input(false);
  public readonly isStaff = input(false);
  public readonly isFrontDesk = input(false);
  public readonly isBar = input(false);
  public readonly isKiosk = input(false);
  public readonly isLocationEnabled = input(true);
  public readonly selectionChange = output<SelectedLocation>();

  public readonly allLocations = this.store.selectSignal(
    CategoryStoreSelectors.selectAllCategoryEntitiesByType('Location')
  );

  public readonly location = computed(() => {
    const locationId = this.locationId();
    const locations = this.allLocations();
    return locations.find((location) => location.id === locationId) ?? null;
  });

  private readonly locationId = signal<string | undefined>(undefined);

  public readonly spareControl = new UntypedFormControl(true);

  private readonly subscriptions: Subscription[] = [];
  private initialisedSpareFromInput = false;

  constructor() {
    effect(() => {
      const selection = this.selection();
      if (selection) {
        this.locationId.set(selection.locationId);
        if (!this.initialisedSpareFromInput) {
          const isSpare = this.isSpare();
          this.spareControl.setValue(isSpare || (isSpare !== false && !selection.roomId));
          this.initialisedSpareFromInput = true;
        }
      }
    });
  }

  public roomUpdated(event: Room) {
    this.selection().roomId = event.id;
    this.emitSelection();
  }

  public locationUpdated(event: Location) {
    this.locationId.set(event.id);
    this.selection().locationId = event.id;
    this.emitSelection();
  }

  private emitSelection() {
    const requiresLocationIdOnly =
      this.spareControl.value ||
      this.isHousekeeping() ||
      this.isStaff() ||
      this.isFrontDesk() ||
      this.isBar() ||
      this.isKiosk();

    const selection = this.selection();
    if (requiresLocationIdOnly) {
      selection.roomId = undefined;
    }

    selection.isValid = requiresLocationIdOnly ? !!selection.locationId : !!selection.locationId && !!selection.roomId;
    this.selectionChange.emit(selection);
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.spareControl.valueChanges.subscribe(() => {
        this.emitSelection();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => {
      if (!subscription.closed) {
        subscription.unsubscribe();
      }
    });
  }
}
